import { some, forEach } from 'lodash';
import React from 'react';

import connect from 'components/lib/connect';
import FeatureNotification from './feature_notification';
import FeatureNotificationTypes from 'scripts/domain/models/agent_notification/feature_notification_types';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';
import { orgIdsWithSidekick, orgsIdsWithoutSidekick } from './org_ids';

const answerThreadsWebinar = {
  feature: FeatureNotificationTypes.AT_WEBINAR,
  roleFlags: ['teamManagement'],
  orgIds: orgIdsWithSidekick,
  key: 'at-webinar',
  text: 'Get Started With Answer Threads',
  subtext:
    'Join our webinar on 9/12 to see how you can seamlessly integrate our new Gen AI feature into your Gladly instance in hours not days.',
  url: 'https://gladly.zoom.us/webinar/register/WN_KOjhXBBwQQmTjg1zxID8vg',
  urlText: 'Register',
  endTime: 1726124399000,
};
const answerThreadsLearnMore = {
  feature: FeatureNotificationTypes.AT_LEARN_MORE,
  roleFlags: ['teamManagement'],
  orgIds: orgsIdsWithoutSidekick,
  key: 'at-learn-more',
  text: 'Say Hello to Answer Threads',
  subtext:
    'Learn how our new Gen AI feature saves your agents time and your business money by providing exceptional CS at scale.',
  url: 'https://connect.gladly.com/docs/gladly-sidekick/article/answer-threads-introduction/',
  urlText: 'Learn more',
  endTime: 1726124399000,
};

const CURRENT_FEATURES = [answerThreadsLearnMore, answerThreadsWebinar];

export function FeatureNotificationList({ agentPreferences, organization }) {
  const isFeatureEnabled = useIsFeatureEnabled();

  let featureNotifications = [];
  forEach(CURRENT_FEATURES, featureProps => {
    if (shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization)) {
      featureNotifications.push(<FeatureNotification {...featureProps} />);
    }
  });

  if (!featureNotifications.length) {
    return null;
  }

  return <React.Fragment>{featureNotifications}</React.Fragment>;
}

const FeatureNotificationListContainer = connect(mapStateToProps)(FeatureNotificationList);

function mapStateToProps({ getProvider }) {
  const agentPreferences = getProvider('agentPreferences').get();
  const organization = getProvider('auth').get()?.claims;

  return {
    agentPreferences,
    organization,
  };
}

export function useHasFeaturesToShow(agentPreferences, organization) {
  const isFeatureEnabled = useIsFeatureEnabled();
  return some(CURRENT_FEATURES, featureProps => {
    return shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization);
  });
}

function shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization) {
  const roleRestricted = featureProps.roleFlags && featureProps.roleFlags.length;
  const restrictedAndHasRole = some(featureProps.roleFlags, roleFlag => isFeatureEnabled(roleFlag));
  const flagRestricted = featureProps.flag;

  const orgRestricted = !!featureProps.orgIds;
  const belongsToOrg = some(featureProps.orgIds, orgId => orgId === organization?.orgId);

  const currentTime = Date.now();
  const startRestricted = !!featureProps.startTime;
  const afterStart = startRestricted ? currentTime > featureProps.startTime : false;
  const endRestricted = !!featureProps.endTime;
  const beforeEnd = endRestricted ? currentTime < featureProps.endTime : false;

  return (
    (!orgRestricted || belongsToOrg) &&
    (!roleRestricted || restrictedAndHasRole) &&
    (!flagRestricted || isFeatureEnabled(featureProps.flag)) &&
    (!startRestricted || afterStart) &&
    (!endRestricted || beforeEnd) &&
    !agentPreferences.hasSeenFeatureNotification(featureProps.feature)
  );
}

export default FeatureNotificationListContainer;
